/**
 * THE AREA HIGHLIGHT STUDY, HEADLESS — one planetary condition in, shaded intervals out.
 *
 * The Copilot's `study.run:areaHighlight` path. `AreaHighlightDialog` is the same seven studies
 * behind a form; this is the half that can run without one, so "highlight when Sun is in Virgo"
 * paints the chart instead of opening a modal somebody then has to fill in and press.
 *
 * <h2>⚠ SEVEN STUDIES BEHIND ONE TOOL, and the endpoint is NOT derivable from the fields</h2>
 *
 * `/api/area-highlight-study` hosts seven POST paths and they do not share a request shape — one
 * takes a sign INDEX, one a nakshatra NAME, one a nakshatra and a charan, one a degree pair, and
 * three take a planet alone. So the type is an explicit field rather than something inferred from
 * which keys happen to be present: inferring it would make "Mercury retrograde" and "Mercury in
 * Virgo" differ by the absence of a key, and a missing key is exactly what a model gets wrong.
 *
 * <h2>⚠ THE SIGN IS A ZERO-BASED INDEX ON THE WIRE, and `rashiIndex` is ONE-based</h2>
 *
 * `PlanetInSignStudyService` reads `Integer.parseInt(req.getSign())` against a table that starts
 * `0 -> Aries`, and the dialog sends `SIGNS.indexOf(name)` to match. `utils/zodiacIndex.js`
 * answers 1-12 because it was written for the MOA "Add value" box, where Libra is the 7th sign.
 * Subtracting one is therefore not a tidy-up, it is the conversion — and getting it wrong does
 * not error: the study runs and shades the NEXT sign along, which on a chart is indistinguishable
 * from a correct answer about a different question.
 *
 * <h2>⚠ AND IT ANSWERS WITH TWO-ELEMENT ARRAYS, which `pairDates` exists for</h2>
 *
 * The recorded trap, from the cycle pillars that call the same endpoint: these responses carry
 * `datePairs` as arrays where other studies answer with objects, and reading `.startDate` off an
 * array yields `undefined` for every interval and draws nothing, without erroring anywhere.
 */

import { pairDates } from './cycleBandGating';
// `nakshatraIndex` is deliberately NOT imported: the nakshatra endpoint takes the NAME where the
// sign endpoint takes an index, which is the asymmetry the two branches below exist for.
import { NAKSHATRAS, RASHIS, rashiIndex } from './zodiacIndex';

/** The frames the dialog offers, which are what the endpoints understand. */
export const AREA_FRAMES = Object.freeze(['geo-sidereal', 'geo-tropical']);

/** Everything a planet can be asked about, and where each question is answered. */
export const AREA_HIGHLIGHT_TYPES = Object.freeze({
  sign: { path: 'planet-in-sign', needs: ['planet', 'sign'], framed: true },
  nakshatra: { path: 'planet-in-nakshatra', needs: ['planet', 'nakshatra'], framed: true },
  nakshatraCharan: {
    path: 'planet-in-nakshatra-charan', needs: ['planet', 'nakshatra', 'charan'], framed: true,
  },
  degreeRange: {
    path: 'runAreaHighlightStudy', needs: ['planet', 'fromDegree', 'toDegree'], framed: true,
  },
  retroComplement: { path: 'planet-retro-complement', needs: ['planet'], framed: false },
  directToRetrograde: { path: 'planet-direct-retro', needs: ['planet'], framed: false },
  retrogradeToDirect: { path: 'planet-retro-direct', needs: ['planet'], framed: false },
});

export const AREA_HIGHLIGHT_TYPE_KEYS = Object.freeze(Object.keys(AREA_HIGHLIGHT_TYPES));

/** The twelve planets the dialog offers. Ketu and Rahu included; the retro studies take neither. */
const PLANETS = Object.freeze([
  'Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn',
  'Uranus', 'Neptune', 'Pluto', 'Rahu', 'Ketu',
]);

/**
 * ⚠ THE THREE BODIES THAT CANNOT GO RETROGRADE, refused rather than asked about.
 *
 * The dialog simply does not offer them (`RETRO_PLANETS` filters them out), so a headless path
 * that passed them would be reaching a combination no form can produce — and the ephemeris would
 * answer with an empty interval list, which reads on screen as "nothing happened" rather than as
 * "that question has no answer".
 */
const NEVER_RETROGRADE = Object.freeze(['Sun', 'Moon', 'Rahu', 'Ketu']);

const RETRO_TYPES = Object.freeze(['retroComplement', 'directToRetrograde', 'retrogradeToDirect']);

/** Case-insensitive, trimmed match against a list, or null. */
function matchIn(list, raw) {
  if (typeof raw !== 'string') return null;
  const key = raw.trim().toLowerCase().replace(/\s+/g, ' ');
  return list.find((n) => n.toLowerCase() === key) || null;
}

/**
 * A frame in the endpoint's own spelling, or null.
 *
 * <p>Accepts what a person says — "sidereal", "tropical" — as well as the wire value, because the
 * model is relaying a sentence and `geo-sidereal` is not a phrase anybody types. "geocentric" is
 * NOT accepted: both frames here are geocentric, so it names the half that does not vary and
 * taking it for one of them would be a guess.
 */
export function resolveFrame(raw) {
  if (typeof raw !== 'string') return null;
  const key = raw.trim().toLowerCase();
  if (key === '') return null;
  if (AREA_FRAMES.includes(key)) return key;
  if (key === 'sidereal' || key === 'geo sidereal') return 'geo-sidereal';
  if (key === 'tropical' || key === 'geo tropical') return 'geo-tropical';
  return null;
}

/**
 * Which of the seven studies a request is for, or null.
 *
 * <p>Accepts the dialog's own `studyType` spellings as well as the short keys, because those are
 * what the activity log and this codebase's own prose use — `planetInSign` reads as the obvious
 * name for `sign` and refusing it would be refusing the reader's vocabulary.
 */
export function resolveHighlightType(raw) {
  if (typeof raw !== 'string') return null;
  const key = raw.trim().toLowerCase().replace(/[\s_-]+/g, '');
  const found = AREA_HIGHLIGHT_TYPE_KEYS.find((k) => k.toLowerCase() === key);
  if (found) return found;
  const aliases = {
    planetinsign: 'sign',
    planetinnakshatra: 'nakshatra',
    planetinnakshatracharan: 'nakshatraCharan',
    charan: 'nakshatraCharan',
    planetindegreerange: 'degreeRange',
    degree: 'degreeRange',
    planetretrocomplement: 'retroComplement',
    retrograde: 'retroComplement',
    retro: 'retroComplement',
    directtoretro: 'directToRetrograde',
    retrotodirect: 'retrogradeToDirect',
  };
  return aliases[key] || null;
}

/**
 * Turns a Copilot request into something the endpoints accept.
 *
 * @returns `{ ok, type?, path?, payload?, applied, problem? }` — `applied` names every default
 *          this filled in, so the explanation can say so rather than implying the reader chose it.
 */
export function resolveAreaHighlightParams(req = {}) {
  const applied = [];

  const type = resolveHighlightType(req.highlightType ?? req.type ?? req.study);
  if (!type) {
    return {
      ok: false,
      applied,
      problem: `Say which area-highlight study to run — one of ${AREA_HIGHLIGHT_TYPE_KEYS.join(', ')}.`,
    };
  }
  const spec = AREA_HIGHLIGHT_TYPES[type];

  const planet = matchIn(PLANETS, req.planet ?? req.body1 ?? req.body);
  if (!planet) {
    return { ok: false, applied, problem: `"${req.planet ?? req.body1 ?? ''}" is not a planet `
      + `this study knows. Use one of: ${PLANETS.join(', ')}.` };
  }
  if (RETRO_TYPES.includes(type) && NEVER_RETROGRADE.includes(planet)) {
    // Refused with the reason, not answered with an empty list. See NEVER_RETROGRADE.
    return { ok: false, applied, problem: `${planet} never goes retrograde, so there is no `
      + 'retrograde interval to shade. The retrograde studies cover Mercury through Pluto.' };
  }

  const payload = { planet };

  if (spec.framed) {
    const frame = resolveFrame(req.frame ?? req.zodiac ?? req.zodiacType);
    if (frame) {
      payload.zodiac = frame;
    } else {
      // SIDEREAL, matching the dialog's own first option rather than a preference: a reader who
      // does not name a zodiac gets what the form would have given them unpressed.
      payload.zodiac = AREA_FRAMES[0];
      applied.push('sidereal zodiac');
    }
  }

  if (type === 'sign') {
    const sign = matchIn(RASHIS, req.sign);
    if (!sign) {
      return { ok: false, applied,
        problem: `"${req.sign ?? ''}" is not one of the twelve signs.` };
    }
    // ⚠ ZERO-BASED. See the header: `rashiIndex` answers 1-12 for the MOA value box, and this
    // endpoint's table starts at 0. Off by one shades the NEXT sign and never errors.
    payload.sign = rashiIndex(sign) - 1;
  }

  if (type === 'nakshatra' || type === 'nakshatraCharan') {
    const nakshatra = matchIn(NAKSHATRAS, req.nakshatra);
    if (!nakshatra) {
      return { ok: false, applied,
        problem: `"${req.nakshatra ?? ''}" is not one of the twenty-seven nakshatras.` };
    }
    // The NAME, not an index — this endpoint differs from the sign one, which is why the two are
    // separate branches rather than one shared "position" field.
    payload.nakshatra = nakshatra;
  }

  if (type === 'nakshatraCharan') {
    const charan = Number(req.charan);
    if (!Number.isInteger(charan) || charan < 1 || charan > 4) {
      return { ok: false, applied, problem: 'A charan is 1, 2, 3 or 4.' };
    }
    payload.charan = charan;
  }

  if (type === 'degreeRange') {
    const from = Number(req.fromDegree);
    const to = Number(req.toDegree);
    if (!Number.isFinite(from) || !Number.isFinite(to)) {
      return { ok: false, applied,
        problem: 'A degree range needs fromDegree and toDegree, in degrees.' };
    }
    // Strings, because that is what the dialog sends and what the DTO declares — a number would
    // be coerced by Jackson today and is one refactor from not being.
    payload.fromDegree = String(from);
    payload.toDegree = String(to);
  }

  return { ok: true, type, path: `/api/area-highlight-study/${spec.path}`, payload, applied };
}

/**
 * The window the study is asked about, and the horizon the bands may reach.
 *
 * <p>⚠ THE END IS THE EXTENDED AXIS, NOT THE LAST TRADED BAR — the same choice the cycle pillars
 * make and for the same reason: a planet crossing a sign is deterministic ephemeris, so an
 * interval returned for next March is as real as one returned for 1998, and asking only as far as
 * the last bar would withhold intervals the chart has columns for.
 */
export function areaHighlightWindow(barData, extendedBarData) {
  const dates = barData?.dates;
  if (!Array.isArray(dates) || dates.length === 0) return null;
  const padded = extendedBarData?.dates;
  const horizon = Array.isArray(padded) && padded.length ? padded : dates;
  return { startDateTime: dates[0], endDateTime: horizon[horizon.length - 1] };
}

/**
 * The fill a Copilot-run highlight gets when the reader did not name one.
 *
 * <p>The dialog's own first swatch, so a headless run and a form run of the same question look
 * the same on the chart. 0.55 alpha is the whole point of that list: a shaded region has to let
 * the candles through it or it is a rectangle laid over the answer.
 */
export const AREA_HIGHLIGHT_DEFAULT_COLOR = 'rgba(30, 136, 229, 0.55)';

/**
 * The group every interval of one question shares.
 *
 * <p>⚠ DERIVED FROM THE LABEL, not minted fresh per run, and that is what makes asking twice
 * REPLACE rather than stack. A reader changing their mind about the zodiac asks the same question
 * again; with a fresh id each time the chart would carry both answers under one pill, and no
 * reading of the shading would tell you which bands the label was about.
 *
 * <p>Two genuinely different questions still get their own group, because their labels differ —
 * which is the same reason the label is the key rather than the planet.
 */
export const areaHighlightGroupId = (label) =>
  `areaHighlight-${String(label).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`;

/** `Sun in Virgo (Sidereal)` — what the pill and the chart label say. */
export function areaHighlightLabel(type, payload) {
  const frame = payload.zodiac === 'geo-tropical' ? 'Tropical' : 'Sidereal';
  const framed = AREA_HIGHLIGHT_TYPES[type]?.framed;
  const suffix = framed ? ` (${frame})` : '';
  switch (type) {
    case 'sign':
      return `${payload.planet} in ${RASHIS[payload.sign]}${suffix}`;
    case 'nakshatra':
      return `${payload.planet} in ${payload.nakshatra}${suffix}`;
    case 'nakshatraCharan':
      return `${payload.planet} in ${payload.nakshatra} Charan ${payload.charan}${suffix}`;
    case 'degreeRange':
      return `${payload.planet} ${payload.fromDegree}–${payload.toDegree}°${suffix}`;
    case 'retroComplement':
      return `${payload.planet} retrograde`;
    case 'directToRetrograde':
      return `${payload.planet} direct → retrograde`;
    case 'retrogradeToDirect':
      return `${payload.planet} retrograde → direct`;
    default:
      return payload.planet;
  }
}

/**
 * `datePairs` as overlays the chart already knows how to draw.
 *
 * <p>⚠ EVERY PAIR GOES THROUGH `pairDates`. These endpoints answer with two-element ARRAYS and
 * the sibling studies answer with objects; reading `.startDate` off an array gives `undefined`
 * for both ends and silently draws nothing. One exported reader, for the reason it records.
 *
 * <p>⚠ AND THE LAST INTERVAL IS NOT STRETCHED TO THE HORIZON. If the study says the condition
 * ends eleven days before the axis does, the band ends there. Stretching it would shade eleven
 * days of "Sun in Virgo" on dates the Sun is not in Virgo — a fabrication drawn in the same pen
 * as the ephemeris and indistinguishable from it.
 */
export function areaHighlightOverlays(datePairs, { groupId, label, color, source = null }) {
  const pairs = Array.isArray(datePairs) ? datePairs : [];
  const out = [];
  pairs.forEach((pair, i) => {
    const [start, end] = pairDates(pair);
    if (!start || !end) return;
    out.push({
      type: 'areaHighlight',
      id: `${groupId}-${i}`,
      groupId,
      date1: start,
      date2: end,
      color,
      // ⚠ WHAT PRODUCED THIS BAND, carried rather than re-derived. The header pill's gear reopens
      // the dialog pre-filled with this question, and the only other way to know what the
      // question was is to parse the LABEL back into fields — a second derivation of something
      // already known, which would disagree with the first the moment a label is reworded.
      source,
      // ⚠ THE LABEL IS ON THE FIRST BAND ONLY. The renderer writes it across the shaded region,
      // so putting it on every interval prints the same words over every band on the chart —
      // which for a fast planet is dozens of overlapping copies. The pill in the header is what
      // names the whole set now; this is for a reader looking at the chart alone.
      label: i === 0 ? label : '',
    });
  });
  return out;
}

/** Names what ran, what was defaulted, and how much was found. */
export function explainAreaHighlightRun(type, payload, applied = [], count = null) {
  const parts = [`Highlighted ${areaHighlightLabel(type, payload)}`];
  if (count !== null) {
    parts.push(count === 0
      ? ' — the study found no intervals in the charted range'
      : ` — ${count} interval${count === 1 ? '' : 's'}`);
  }
  if (applied.length > 0) parts.push(` (defaulted to ${applied.join(', ')})`);
  return `${parts.join('')}.`;
}

/**
 * The area highlights currently on the chart, one entry per STUDY RUN.
 *
 * <p>⚠ DEDUPED BY `runId` WHEN THERE IS ONE, falling back to `groupId`. A question with
 * twenty-three intervals is twenty-three overlays and ONE thing a reader is looking at. A pill per
 * overlay would put twenty-three identical chips in the header for one answer.
 *
 * <h2>⚠ IT WAS `groupId` ONLY, AND THAT IS THE BUG THIS FIXES</h2>
 *
 * The intent above has always been right and the KEY was wrong.
 * `addAreaHighlightOverlaysAndMessages` stamps `groupId` as `label-start-end` — deliberately
 * unique per date pair, so undo can take one PTA cross at a time — so grouping by it produced
 * exactly the per-interval explosion this docblock warned against. Measured on a live Sun-in-Gemini
 * run: **34 pills, 2991px wide, wrapping to a second row of the header.**
 *
 * <p>So the two groupings are now two fields, and this reads the coarser one. The `groupId`
 * fallback is not politeness: the PTA/PTB cycle pillars and anything drawn before `runId` existed
 * carry no run, and dropping them from the bar would silently remove a reader's only record of
 * them.
 *
 * <p>⚠ THE RETURNED `groupId` IS THE GROUPING KEY, which may be a run id. It is the value handed
 * to `onRemoveGroup`, so that handler has to match EITHER field — see
 * `App.handleRemoveOverlayGroup`. Renaming the returned field would have been tidier and would
 * have broken every existing caller and test id for no gain.
 *
 * <p>The first overlay of each group is the one that carries the label and the source — see
 * `areaHighlightOverlays`, which labels only the first so the chart is not printed over.
 */
export function activeAreaHighlights(overlays) {
  const list = Array.isArray(overlays) ? overlays : [];
  const byGroup = new Map();
  list.forEach((o) => {
    if (!o || o.type !== 'areaHighlight') return;
    // A run id groups a whole study; a group id groups one period. Prefer the former.
    const key = o.runId || o.groupId;
    if (!key) return;
    if (!byGroup.has(key)) {
      byGroup.set(key, {
        groupId: key, label: o.label || '', color: o.color, source: o.source || null,
        count: 0,
      });
    }
    const entry = byGroup.get(key);
    entry.count += 1;
    // The label and the source live on the FIRST interval only, and map iteration order is
    // insertion order — but an overlay list can be rebuilt in any order by a load or an undo, so
    // whichever member carries them wins rather than whichever arrived first.
    if (!entry.label && o.label) entry.label = o.label;
    if (!entry.source && o.source) entry.source = o.source;
  });
  return [...byGroup.values()];
}

/**
 * The dialog prefill that reopens a highlight for editing — the pill's gear.
 *
 * <p>⚠ THE SIGN BECOMES A NAME AGAIN. The wire carries a zero-based position and the form holds
 * the string its dropdown shows, so this is the inverse of the conversion
 * `resolveAreaHighlightParams` makes. Handing the index to the form would select nothing and
 * render an empty picker, which reads as a prefill that failed rather than one that was wrong.
 *
 * <p>Returns null for a highlight with no recorded source — a band drawn before this existed, or
 * by the cycle pillars, which share the overlay type but are not this study. The caller shows no
 * gear in that case rather than opening a dialog it cannot fill.
 */
export function prefillForAreaHighlight(source) {
  if (!source || typeof source !== 'object') return null;
  const { type, payload } = source;
  if (!type || !payload) return null;

  switch (type) {
    case 'sign':
      return {
        planetSignPlanet: payload.planet,
        planetSignSign: RASHIS[payload.sign],
        planetSignZodiac: payload.zodiac,
      };
    case 'nakshatra':
      return {
        planetNakshatraPlanet: payload.planet,
        planetNakshatra: payload.nakshatra,
        planetNakshatraZodiac: payload.zodiac,
      };
    case 'nakshatraCharan':
      return {
        planetCharanPlanet: payload.planet,
        planetCharanNakshatra: payload.nakshatra,
        planetCharanCharan: payload.charan,
        planetCharanZodiac: payload.zodiac,
      };
    case 'degreeRange':
      return {
        planetFrom: payload.planet,
        fromDegree: payload.fromDegree,
        toDegree: payload.toDegree,
        zodiacType1: payload.zodiac,
      };
    case 'retroComplement':
      return { retroComplementPlanet: payload.planet };
    case 'directToRetrograde':
      return { directPlanet: payload.planet };
    case 'retrogradeToDirect':
      return { retroPlanet: payload.planet };
    default:
      return null;
  }
}
